import { useQuery } from '@apollo/react-hooks';
import { createStyles, withStyles, WithStyles } from '@material-ui/core';
import * as Sentry from '@sentry/browser';
import { loader } from 'graphql.macro';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import last from 'lodash/last';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import qs from 'querystringify';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { colors } from 'src/themes/colors';
import { openPopUp } from 'src/helpers/openCenteredPopUp';
import { formatAvailableHours } from 'src/helpers/sessionsHelpers';
import { getCurrentUser } from 'src/selectors/userSelectors';
import CircleArrowUp from 'src/assets/svgs/circle-arrow-up.svg';

import PageContainer from '../components/PageContainer';
import { AvailabilityHeader } from '../components/Sessions/AvailabilityHeader';
import { AvailableHoursModal } from '../components/Sessions/AvailableHoursModal';
import { CalendarModal } from '../components/Sessions/CalendarModal/index';
import { HowManyClientsModal } from '../components/Sessions/HowManyClientsModal';
import { PickSessionTypeModal } from '../components/Sessions/PickSessionTypeModal';
import SessionsList from '../components/Sessions/SessionsList/SessionsList';
import { memberClient } from '../config/Apollo';
import { AvailableHourRange, CalendarProfile } from '../models/Sessions';

const updateCalendarMutation = loader('../components/Sessions/documents/UpdateCalendar.gql');

const styles = () =>
  createStyles({
    loadingIndicator: {
      position: 'fixed',
      top: '50%',
      left: '50%',
    },
    sessionsContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      flex: 1,
      height: '100%',
      overflowY: 'auto',
    },
    body: {
      paddingLeft: 32,
      paddingRight: 32,
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
    linkPrompt: {
      marginTop: 20,
      padding: 5,
    },
    scrollToTop: {
      position: 'absolute',
      bottom: 100,
      right: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '21px 11px 15px 13px',
      border: `1px solid ${colors.black2}`,
      borderRightWidth: 0,
      borderRadius: '4px 0 0 4px',
      cursor: 'pointer',
    },
    scrollToTopImage: {
      marginBottom: 14,
      maxWidth: 41,
    },
    scrollToTopText: {
      color: colors.black2,
      fontWeight: 900,
    },
  });

export const CALENDAR_QUERY = loader('./documents/CalendarQuery.gql');
const GET_AUTH_LINK_QUERY = loader('./documents/GetAuthLinkQuery.gql');
const LINK_CALENDAR_MUTATION = loader('./documents/LinkCalendarMutation.gql');

export interface CalendarData {
  availableHours: AvailableHourRange[];
  profiles: CalendarProfile[];
}

interface QueryParams {
  code?: string;
}

enum SessionsPageTestID {
  Container = 'SessionsPageTestID.Container',
}

type Props = WithStyles<typeof styles> & RouteComponentProps;

const SessionsPage: React.FC<Props> = ({ classes, location }) => {
  const [workingHoursModalOpen, setWorkingHoursModalOpen] = useState(false);
  const [howManyClientsModalOpen, setHowManyClientsModalOpen] = useState(false);
  const [pickSessionTypeModalOpen, setPickSessionTypeModalOpen] = useState(false);
  const [newCalendarConnectSuccess, setNewCalendarConnectSuccess] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarData, setCalendarData] = useState<CalendarData>();
  const [hasAvailableHours, setHasAvailableHours] = useState(
    !!calendarData?.availableHours.length || false,
  );
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  const user = useSelector(getCurrentUser);

  const params = qs.parse(location.search) as QueryParams;
  const redirectUri = window.location.href.split('?')[0];
  const [isScrollToTopVisible, setScrollToTopVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    refetch: refetchCalendarData,
    loading: calendarDataLoading,
    data,
  } = useQuery(CALENDAR_QUERY, {
    client: memberClient,
    variables: {
      sync: false,
    },
  });

  useLayoutEffect(() => {
    const handleScroll = (e) => {
      setScrollToTopVisible(e.target.scrollTop > 500);
    };

    if (containerRef.current) {
      containerRef.current.addEventListener('scroll', handleScroll);
    }

    return () => containerRef.current?.removeEventListener('scroll', handleScroll);
  }, [containerRef, setScrollToTopVisible]);

  useEffect(() => {
    const updatedData: CalendarData = { ...data };
    const availableHours = formatAvailableHours(updatedData?.availableHours);
    updatedData.availableHours = availableHours;
    setCalendarData(updatedData);
  }, [data]);

  const updateAvailableHourRanges = (availableHours: AvailableHourRange[]) => {
    if (calendarData) {
      setCalendarData({
        ...calendarData,
        availableHours,
      });
    }
  };

  const handleWorkingHourModalClose = () => {
    setWorkingHoursModalOpen(false);
  };

  const handleCalendarModalClose = (getStartedMode?: boolean) => {
    refetchCalendarData({
      sync: true,
    });
    setNewCalendarConnectSuccess(false);
    setCalendarModalOpen(false);

    if (getStartedMode) {
      setWorkingHoursModalOpen(true);
    }
  };

  const handleSeeAvailabilityCalendar = () => {
    const apiUri = process.env.REACT_APP_DEFAULT_WEBAPP_FRONTEND_URL;
    if (apiUri) {
      const baseUri = apiUri.replace('/api', '');
      openPopUp(`${baseUri}/availability-readonly/${user.uuid}`);
    }
  };

  const handleUpdateCalendarProfile = async (
    calendarUuid: string,
    state: { isActive: boolean; isTarget: boolean },
  ) => {
    setCalendarData((data) => {
      if (!data) {
        return data;
      }

      const profiles = data.profiles.map((profile) => {
        const calendars = profile.calendars.map((cal) => {
          if (cal.uuid !== calendarUuid) {
            return cal;
          }

          return {
            ...cal,
            isActive: state.isActive,
            isTarget: state.isTarget,
          };
        });

        return {
          ...profile,
          calendars,
        };
      });

      return {
        ...data,
        profiles,
      };
    });
  };

  const handleDeleteCalendarProfile = () => refetchCalendarData();

  const handleCalendarSyncRequest = () => refetchCalendarData({ sync: true });

  const sendToAuthUrl = () => {
    memberClient
      .query({ query: GET_AUTH_LINK_QUERY, variables: { redirectUri } })
      .then((response: { data: { authLink: { url: string } } }) => {
        const url = response.data.authLink.url;
        window.location.assign(url);
      });
  };

  const updateAllNewCalendars = (calendars: any[]) =>
    new Promise<void>((resolve) => {
      if (calendars.length === 0) {
        resolve();
      }

      Promise.all(
        calendars.map(({ uuid }) =>
          memberClient.mutate({
            mutation: updateCalendarMutation,
            variables: { uuid, isActive: true, isTarget: true },
          }),
        ),
      )
        .then(() => resolve())
        .catch(() => resolve());
    });

  const linkCalendar = async (code: string) =>
    new Promise<void>(async (resolve) => {
      try {
        const response = await memberClient.mutate({
          mutation: LINK_CALENDAR_MUTATION,
          variables: { code, redirectUri },
        });
        const linkSuccess = get(response, 'data.linkCronofyCalendar.ok');
        const isNewlyCreated = Number(get(response, 'data.linkCronofyCalendar.status')) === 201;

        if (linkSuccess) {
          const profileData = await refetchCalendarData();
          const profiles = orderBy(
            get(profileData, 'data.profiles', []),
            ({ createdAt }) => moment(createdAt).valueOf(),
            'asc',
          );
          // TODO:(DW) Is there a case where this method is called for a calendar that is _not_ newly created?
          if (isNewlyCreated) {
            const lastProfile = last(profiles);
            const calendars = lastProfile?.calendars || [];

            await updateAllNewCalendars(calendars);
            // After updating isActive/isTarget in the backend, sync and refetch the most current data...
            const { data } = await refetchCalendarData({ sync: true });
            // so we can set it as the new value on the component state
            setCalendarData(data);
          }
        } else {
          throw new Error('Linking calendar failed: linkCronofyCalendar status not OK');
        }

        setNewCalendarConnectSuccess(isNewlyCreated);
        setCalendarModalOpen(true);
      } catch (err: any) {
        err.name = 'Error in SessionsPage>linkCalendar';
        Sentry.captureException(err);
      } finally {
        setAvailabilityLoading(false);
        resolve();
      }
    });

  useEffect(() => {
    setHasAvailableHours(!!calendarData?.availableHours.length);
  }, [calendarData]);

  useEffect(() => {
    // After authorizing, Cronofy will redirect back here with a `code` param, which we use to link
    if (params.code) {
      setAvailabilityLoading(true);
      linkCalendar(params.code).then(() => {
        window.history.replaceState(null, 'Modern Health Provider Dashboard', '/sessions');
      });
    } else {
      refetchCalendarData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchCalendarData, params.code]);

  const missingCheckForConflicts = calendarData?.profiles
    ? flatMap(calendarData?.profiles, (profile) => profile.calendars).filter(
        (calendar) => calendar.isActive === true,
      ).length === 0
    : false;

  const handleScrollTopTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <PageContainer position="inherit">
      <div
        className={classes.sessionsContainer}
        data-errorid={SessionsPageTestID.Container}
        ref={containerRef}
      >
        {isScrollToTopVisible && (
          <div className={classes.scrollToTop} onClick={handleScrollTopTop}>
            <img
              src={CircleArrowUp}
              alt="icon with arrow up"
              className={classes.scrollToTopImage}
            />
            <span className={classes.scrollToTopText}>Back to Top</span>
          </div>
        )}
        <AvailabilityHeader
          setCalendarModalOpen={setCalendarModalOpen}
          setWorkingHoursModalOpen={setWorkingHoursModalOpen}
          setPickSessionTypeModalOpen={setPickSessionTypeModalOpen}
          handleSeeAvailabilityCalendar={handleSeeAvailabilityCalendar}
          missingCheckForConflicts={missingCheckForConflicts}
          loading={calendarDataLoading || availabilityLoading}
          calendarData={calendarData}
        />
        <div className={classes.body}>
          <SessionsList memberClient={memberClient} />
        </div>
        {calendarModalOpen && (
          <CalendarModal
            onSendToAuthUrl={sendToAuthUrl}
            onContinue={() => handleCalendarModalClose(true)}
            onClose={() => handleCalendarModalClose()}
            setNewCalendarConnect={setNewCalendarConnectSuccess}
            onUpdateCalendarProfile={handleUpdateCalendarProfile}
            handleSyncClick={handleCalendarSyncRequest}
            onDeleteCalendarProfile={handleDeleteCalendarProfile}
            hasAvailableHours={hasAvailableHours}
            profiles={calendarData?.profiles}
            newCalendarConnected={newCalendarConnectSuccess}
            missingCheckForConflicts={missingCheckForConflicts}
          />
        )}
        {pickSessionTypeModalOpen && (
          <PickSessionTypeModal
            onClose={() => setPickSessionTypeModalOpen(false)}
            onAddCalendarClick={() => setCalendarModalOpen(true)}
            onAddClientOpeningsClick={() => setHowManyClientsModalOpen(true)}
          />
        )}
        {howManyClientsModalOpen && (
          <HowManyClientsModal
            onClose={() => setHowManyClientsModalOpen(false)}
            onBack={() => {
              setHowManyClientsModalOpen(false);
              setPickSessionTypeModalOpen(true);
            }}
          />
        )}
        <AvailableHoursModal
          showDialog={workingHoursModalOpen}
          onCloseDialog={handleWorkingHourModalClose}
          availableHourRanges={calendarData?.availableHours}
          updateAvailableHourRanges={updateAvailableHourRanges}
        />
      </div>
    </PageContainer>
  );
};

export default withStyles(styles)(SessionsPage);
